import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

type AiStatus = 'processed' | 'transcribed' | 'skipped' | 'failed';
type ProcessingMode = 'full' | 'transcript_only' | 'skip';

export interface MemoryProcessingStatus {
  aiStatus: AiStatus | null;
  processingMode: ProcessingMode | null;
  isProcessingComplete: boolean;
  isTranscriptionComplete: boolean;
  isLoading: boolean;
}

const PROCESSING_COMPLETE = new Set<AiStatus>(['processed', 'skipped', 'failed']);
// 'skipped' is intentionally excluded — a skip-mode capture exits the wait
// screen via the `processingMode === 'skip'` short-circuit in WaitScreen, not
// via this set. A 'transcript_only' capture lands on the explicit
// 'transcribed' state, which is the only signal that means "transcript ready".
const TRANSCRIPTION_COMPLETE = new Set<AiStatus>(['transcribed', 'processed', 'failed']);

export function useMemoryProcessingStatus(memoryId: string | null): MemoryProcessingStatus {
  const [aiStatus, setAiStatus] = useState<AiStatus | null>(null);
  const [processingMode, setProcessingMode] = useState<ProcessingMode | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!memoryId) {
      setAiStatus(null);
      setProcessingMode(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    supabase
      .from('memories')
      .select('id, ai_status, processing_mode')
      .eq('id', memoryId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setAiStatus((data?.ai_status as AiStatus) ?? null);
        setProcessingMode((data?.processing_mode as ProcessingMode) ?? null);
        setIsLoading(false);
      });

    const channel = supabase
      .channel(`memory-processing-status:${memoryId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'memories',
          filter: `id=eq.${memoryId}`,
        },
        (payload: { new: Record<string, unknown> }) => {
          const row = payload.new;
          setAiStatus((row.ai_status as AiStatus) ?? null);
          setProcessingMode((row.processing_mode as ProcessingMode) ?? null);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [memoryId]);

  return {
    aiStatus,
    processingMode,
    isProcessingComplete: aiStatus !== null && PROCESSING_COMPLETE.has(aiStatus),
    isTranscriptionComplete: aiStatus !== null && TRANSCRIPTION_COMPLETE.has(aiStatus),
    isLoading,
  };
}
